import {
  ContentFilterObject,
  ContentSearchResult,
  KnownContent,
  AdminUrlLabelBodyParams,
  AdminDeactivateUserBodyParams,
  AdminSpamOverrideBodyParams,
  AdminExpensiveSourceSummary,
  AdminGetUsersQueryParams,
  AdminRootStatusResponse,
  AdminUpdateUserBodyParams,
  AdminUrlSampleResponse,
  AdminUserCountsResponse,
  AdminUserDetailsResponse,
  AdminUserResponse,
  AdminUsersPaginatedResponse,
  CreateContentBodyParams,
  ImportByUrlQueryParams,
  ImportResponse,
  UserSignInResponse,
  UrlStatus,
  Article,
  AdminEndpoints,
  MainEndpoints,
  AdminSourceResponse,
  AdminSourcesResponse,
  LoginBodyParams,
  LoginMethod,
} from "@authory-packages/core";
import axios, { AxiosRequestConfig } from "axios";
import { AUTH_TOKEN_KEY } from "./constants";
import { merge } from "lodash";

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "",
});

const addAuthHeader = (config: AxiosRequestConfig) => {
  const token = localStorage.getItem(AUTH_TOKEN_KEY);
  if (token) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  return config;
};

// Request interceptor - append authorization header to every request done with axios based on the cookie value
let interceptorNumber = axiosInstance.interceptors.request.use(addAuthHeader);

export class Api {
  static get<T, P = {}>(url: string, params?: P) {
    return axiosInstance.get<T>(url, { params });
  }

  static post<T, P = {}>(url: string, data?: P, options?: AxiosRequestConfig) {
    const mergedOptions = merge(
      {},
      { headers: { "Content-Type": "application/json" } },
      options,
    );
    return axiosInstance.post<T>(url, data || {}, mergedOptions);
  }

  static put<T, P = {}>(url: string, data?: P, options?: AxiosRequestConfig) {
    const mergedOptions = merge(
      {},
      { headers: { "Content-Type": "application/json" } },
      options,
    );
    return axiosInstance.put<T>(url, data || {}, mergedOptions);
  }

  static delete<T, P = {}>(
    url: string,
    data?: P,
    options?: AxiosRequestConfig,
  ) {
    const mergedOptions = merge(
      {},
      { headers: { "Content-Type": "application/json" } },
      options,
    );
    return axiosInstance.delete<T>(url, mergedOptions);
  }

  static getContentGeneric(params: ContentFilterObject) {
    return Api.get<ContentSearchResult, ContentFilterObject>(
      `admin/content`,
      params,
    );
  }

  static getContent(urlid: number) {
    return Api.get<{ urls: KnownContent[] }, ContentFilterObject>(
      `admin/content`,
      { urlid },
    );
  }

  static getSource(userSlug: string, websiteSlug: string) {
    return Api.get<AdminSourceResponse>(
      `admin/website/${encodeURIComponent(userSlug)}/${encodeURIComponent(
        websiteSlug,
      )}`,
    );
  }

  static getContentForSource(
    sourceid: number,
    searchText: string,
    take: number = 1,
    compact: boolean = true,
  ) {
    return Api.get<{ urls: KnownContent[] }, ContentFilterObject>(
      `admin/content`,
      { sourceid, url: searchText, take },
    );
  }

  static getAllContentForSource(
    sourceid: number,
    take: number,
    compact: boolean = true,
  ) {
    return Api.get<{ urls: KnownContent[] }, ContentFilterObject>(
      `admin/content`,
      { sourceid, take },
    );
  }

  static login(email: string, password: string) {
    const body: LoginBodyParams = {
      payload: {
        type: LoginMethod.Password,
        email,
        password,
        turnstileToken: "ignored",
      },
    };

    return Api.post<UserSignInResponse>(`signin`, body);
  }

  static me() {
    return Api.get<AdminUserResponse>(`/user`);
  }

  static impersonate(userSlug: string) {
    return Api.get<{ token: string; redirectTo: string }>(
      `/admin/user/${encodeURIComponent(userSlug)}/impersonate`,
    );
  }

  static getUsers(params: AdminGetUsersQueryParams) {
    return Api.get<AdminUsersPaginatedResponse>(`/admin/users`, {
      skip: params.skip,
      take: params.take,
      email: params.email,
      slug: params.slug,
      name: params.name,
      stage: params.stage,
      subscriptionPlanKey: params.subscriptionPlanKey,
      subscriptionStatus: params.subscriptionStatus,
      loginMethod: params.loginMethod,
      hasNoSubscriptionPlan: params.hasNoSubscriptionPlan,
    });
  }

  static getCounts() {
    return Api.get<AdminUserCountsResponse>(`/admin/user-counts`);
  }

  static getUnlabelledUrlSamples() {
    return Api.get<AdminUrlSampleResponse>(
      AdminEndpoints.MlFetchRandomUrlSample,
    );
  }

  static async setMlLabel(id: number, isContent: boolean) {
    return await Api.post<{}, AdminUrlLabelBodyParams>(
      AdminEndpoints.MlSetLabel,
      {
        urlId: id,
        isContent,
      },
    );
  }

  static getUser(userSlug: string) {
    return Api.get<AdminUserDetailsResponse>(
      `/admin/user/${encodeURIComponent(userSlug)}`,
    );
  }

  static getUserWebsites(userSlug: string) {
    return Api.get<AdminSourcesResponse>(
      `/admin/websites/${encodeURIComponent(userSlug)}`,
    );
  }

  static getUserWebsite(userSlug: string, websiteSlug: string) {
    return Api.get<AdminSourceResponse>(
      `/admin/website/${encodeURIComponent(userSlug)}/${websiteSlug}`,
    );
  }

  static async findContent(params: ImportByUrlQueryParams, token: string) {
    axiosInstance.interceptors.request.eject(interceptorNumber);

    try {
      const res = await axiosInstance.get<ImportResponse>(
        MainEndpoints.FindContent,
        {
          params,
          headers: {
            Authorization: "Bearer " + token,
          },
        },
      );

      interceptorNumber = axiosInstance.interceptors.request.use(addAuthHeader);

      return res;
    } catch (error: any) {
      //clean interceptor even if request fails
      interceptorNumber = axiosInstance.interceptors.request.use(addAuthHeader);

      throw error;
    }
  }

  static async createSource(userSlug: string, data: any) {
    const res = await Api.post<AdminSourceResponse>(
      `/admin/website/${encodeURIComponent(userSlug)}/create`,
      data,
    );
    return res;
  }

  static async createContent(params: CreateContentBodyParams, token: string) {
    axiosInstance.interceptors.request.eject(interceptorNumber);

    try {
      const res = await Api.post<Article>(MainEndpoints.CreateContent, params, {
        headers: {
          Authorization: "Bearer " + token,
        },
      });

      interceptorNumber = axiosInstance.interceptors.request.use(addAuthHeader);

      return res;
    } catch (error: any) {
      // clean interceptor even if request fails
      interceptorNumber = axiosInstance.interceptors.request.use(addAuthHeader);

      throw error;
    }
  }

  static async saveUser(userSlug: string, params: AdminUpdateUserBodyParams) {
    const res = await Api.post<AdminUserDetailsResponse>(
      `/admin/user/${encodeURIComponent(userSlug)}/update`,
      params,
    );
    return res;
  }

  static async setStrategy(userSlug: string, websiteSlug: string, data: any) {
    return await Api.post(
      `/admin/website/${encodeURIComponent(userSlug)}/${encodeURIComponent(
        websiteSlug,
      )}/strategy/set`,
      data,
    );
  }

  static async updateSource(userSlug: string, websiteSlug: string, data: any) {
    return await Api.post(
      `/admin/website/${encodeURIComponent(userSlug)}/${encodeURIComponent(
        websiteSlug,
      )}/update`,
      data,
    );
  }

  static async moveURLS(
    userSlug: string,
    websiteSlug: string,
    from: UrlStatus,
    to: UrlStatus,
  ) {
    return await Api.post<{ status: string; count: number }>(
      `/admin/website/${encodeURIComponent(userSlug)}/${encodeURIComponent(
        websiteSlug,
      )}/move-urls`,
      { from, to },
    );
  }

  static async deleteSource(userSlug: string, websiteSlug: string) {
    return await Api.post(
      `/admin/website/${encodeURIComponent(userSlug)}/${encodeURIComponent(
        websiteSlug,
      )}/delete`,
      {},
    );
  }

  static async bulkImportPipeline(
    userSlug: string,
    websiteSlug: string,
    urls: string[],
  ) {
    return await Api.post<{
      status: string;
      created: number;
      existing: number;
    }>(
      `/admin/website/${encodeURIComponent(userSlug)}/${encodeURIComponent(
        websiteSlug,
      )}/add-urls`,
      urls,
    );
  }

  static async skipCrawl(userSlug: string, websiteSlug: string) {
    return await Api.post(
      `/admin/website/${encodeURIComponent(userSlug)}/${encodeURIComponent(
        websiteSlug,
      )}/skip-crawl`,
    );
  }

  static async clearCrawl(userSlug: string, websiteSlug: string) {
    return await Api.post(
      `/admin/website/${encodeURIComponent(userSlug)}/${encodeURIComponent(
        websiteSlug,
      )}/clear`,
    );
  }

  static async activateSource(userSlug: string, websiteSlug: string) {
    return await Api.post(
      `/admin/website/${encodeURIComponent(userSlug)}/${encodeURIComponent(
        websiteSlug,
      )}/activate`,
    );
  }

  static async startCrawlSource(userSlug: string, websiteSlug: string) {
    return await Api.post(
      `/admin/website/${encodeURIComponent(userSlug)}/${encodeURIComponent(
        websiteSlug,
      )}/start-crawl`,
    );
  }

  static async activateUser(userSlug: string) {
    return await Api.post(
      `/admin/user/${encodeURIComponent(userSlug)}/activate`,
    );
  }

  static async deactivateUser(
    userSlug: string,
    request: AdminDeactivateUserBodyParams,
  ) {
    return await Api.post(
      `/admin/user/${encodeURIComponent(userSlug)}/deactivate`,
      request,
    );
  }

  static async setSpamOverride(
    userSlug: string,
    request: AdminSpamOverrideBodyParams,
  ) {
    return await Api.post(
      `/admin/user/${encodeURIComponent(userSlug)}/spam-override`,
      request,
    );
  }

  static async reactivateUser(userSlug: string) {
    return await Api.post(
      `/admin/user/${encodeURIComponent(userSlug)}/reactivate`,
    );
  }

  static async deleteUser(userSlug: string) {
    return await Api.post(`/admin/user/${encodeURIComponent(userSlug)}/delete`);
  }

  static async startCrawlUser(userSlug: string) {
    return await Api.post(
      `/admin/user/${encodeURIComponent(userSlug)}/start-crawl`,
    );
  }

  static async getSystemStatus() {
    return await Api.get(`/admin/system/durga-status`);
  }

  static async getExpensiveSources() {
    return await Api.get<AdminExpensiveSourceSummary>(
      `/admin/system/expensive-sources`,
    );
  }

  static async getRootStatus() {
    return await Api.get<AdminRootStatusResponse>(`/admin/system/root-status`);
  }

  static async resolveUserFromRoot(rootId: string) {
    return await Api.get(`/admin/get-user-by-root/${rootId}`);
  }

  static async resolve(id: string) {
    return await Api.get(`/admin/resolve?id=${id}`);
  }

  static async suspendSource(userSlug: string, sourceSlug: string) {
    return await Api.post(
      `/admin/website/${encodeURIComponent(userSlug)}/${encodeURIComponent(
        sourceSlug,
      )}/suspend`,
    );
  }
}
